var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { instanceToPlain, plainToInstance, Type } from "class-transformer";
import { IsBoolean, IsDate, IsOptional } from "class-validator";
import { UnitSystem } from "healthex-common/health.wallet.dtos";
import { StudyTranslationLanguage } from "healthex-common/study-translation-language.dtos";
import { asEnum } from "healthex-common/utils";
export var CookieName;
(function (CookieName) {
    CookieName["PATIENT_COLLECTED_EMAIL"] = "patientCollectedEmail";
})(CookieName || (CookieName = {}));
export var ClearAuthState;
(function (ClearAuthState) {
    ClearAuthState["INACTIVE"] = "INACTIVE";
    ClearAuthState["IN_PROGRESS"] = "IN_PROGRESS";
    ClearAuthState["SUCCESS"] = "SUCCESS";
    ClearAuthState["ERROR"] = "ERROR";
    ClearAuthState["EXITED"] = "EXITED";
})(ClearAuthState || (ClearAuthState = {}));
export var SocialLoginAuthState;
(function (SocialLoginAuthState) {
    SocialLoginAuthState["INACTIVE"] = "INACTIVE";
    SocialLoginAuthState["IN_PROGRESS"] = "IN_PROGRESS";
    SocialLoginAuthState["SUCCESS"] = "SUCCESS";
    SocialLoginAuthState["ERROR"] = "ERROR";
})(SocialLoginAuthState || (SocialLoginAuthState = {}));
export class ClearFallbackData {
    constructor(fields) {
        Object.assign(this, fields);
    }
}
__decorate([
    IsDate(),
    Type(() => Date)
], ClearFallbackData.prototype, "fallbackLastUsedAt", void 0);
export class SandboxLoginData {
    constructor(fields) {
        Object.assign(this, fields);
    }
}
__decorate([
    IsBoolean(),
    IsOptional()
], SandboxLoginData.prototype, "isSandboxLogin", void 0);
export class Persistence {
    setTokenInfo(tokenInfo) {
        if (tokenInfo) {
            localStorage.setItem("tokenInfo", JSON.stringify(tokenInfo));
        }
        else {
            localStorage.removeItem("tokenInfo");
        }
    }
    getTokenInfo() {
        const tokenInfoString = localStorage.getItem("tokenInfo");
        return tokenInfoString ? JSON.parse(tokenInfoString) : null;
    }
    setCookie(name, value, maxAgeInDays, path = "/", secure = true, sameSite = "strict") {
        const encodedName = encodeURIComponent(name);
        const encodedValue = encodeURIComponent(value);
        let cookie = `${encodedName}=${encodedValue}`;
        const maxAgeSeconds = Math.round(maxAgeInDays * 24 * 60 * 60);
        cookie += `; max-age=${maxAgeSeconds}`;
        cookie += `; path=${path}`;
        if (secure)
            cookie += "; Secure";
        cookie += `; SameSite=${sameSite}`;
        document.cookie = cookie;
    }
    getCookieValue(name) {
        const encodedName = `${encodeURIComponent(name)}=`;
        const cookies = document.cookie.split(";");
        for (const cookie of cookies) {
            const trimmed = cookie.trim();
            if (trimmed.startsWith(encodedName)) {
                return decodeURIComponent(trimmed.slice(encodedName.length));
            }
        }
        return null;
    }
    deleteCookie(name, path = "/") {
        document.cookie = `${encodeURIComponent(name)}=; max-age=0; path=${path}`;
    }
    setRememberedLoginData(rememberedLoginData) {
        if (rememberedLoginData) {
            localStorage.setItem("rememberedLoginData", JSON.stringify(rememberedLoginData));
        }
        else {
            localStorage.removeItem("rememberedLoginData");
        }
    }
    getRememberedLoginData() {
        const rememberedLoginDataString = localStorage.getItem("rememberedLoginData");
        return rememberedLoginDataString ? JSON.parse(rememberedLoginDataString) : null;
    }
    getSmartAuthData() {
        const smartAuthDataString = sessionStorage.getItem("smartAuthData");
        return smartAuthDataString ? JSON.parse(smartAuthDataString) : null;
    }
    setSmartAuthData(smartAuthData) {
        if (smartAuthData) {
            sessionStorage.setItem("smartAuthData", JSON.stringify(smartAuthData));
        }
        else {
            sessionStorage.removeItem("smartAuthData");
        }
    }
    getFacilitatedFHIRAuthData() {
        const facilitatedFHIRAuthDataString = sessionStorage.getItem("facilitatedFHIRAuthData");
        return facilitatedFHIRAuthDataString ? JSON.parse(facilitatedFHIRAuthDataString) : null;
    }
    setFacilitatedFHIRAuthData(facilitatedFHIRAuthData) {
        if (facilitatedFHIRAuthData) {
            sessionStorage.setItem("facilitatedFHIRAuthData", JSON.stringify(facilitatedFHIRAuthData));
        }
        else {
            sessionStorage.removeItem("facilitatedFHIRAuthData");
        }
    }
    setLanguage(language) {
        if (language) {
            localStorage.setItem("language", language);
        }
        else {
            localStorage.removeItem("language");
        }
    }
    getLanguage() {
        const language = localStorage.getItem("language");
        if (!language)
            return null;
        return asEnum(StudyTranslationLanguage)(language);
    }
    setUnitSystem(unitSystem) {
        if (unitSystem) {
            localStorage.setItem("unitSystem", unitSystem);
        }
        else {
            localStorage.removeItem("unitSystem");
        }
    }
    getUnitSystem() {
        const value = localStorage.getItem("unitSystem");
        if (!value)
            return null;
        return asEnum(UnitSystem)(value);
    }
    setEnglishStringsDownloaded(studyId) {
        localStorage.setItem(`${studyId}_english_translations_downloaded`, "true");
    }
    removeEnglishStringsDownloaded(studyId) {
        localStorage.removeItem(`${studyId}_english_translations_downloaded`);
    }
    getEnglishStringsDownloaded(studyId) {
        return !!localStorage.getItem(`${studyId}_english_translations_downloaded`);
    }
    setClearAuthData(clearAuthData) {
        if (clearAuthData) {
            sessionStorage.setItem("clearAuthData", JSON.stringify(clearAuthData));
        }
        else {
            sessionStorage.removeItem("clearAuthData");
        }
    }
    getClearAuthData() {
        const clearAuthDataString = sessionStorage.getItem("clearAuthData");
        return clearAuthDataString ? JSON.parse(clearAuthDataString) : null;
    }
    setSocialLoginAuthData(socialLoginAuthData) {
        if (socialLoginAuthData) {
            sessionStorage.setItem("socialLoginAuthData", JSON.stringify(socialLoginAuthData));
        }
        else {
            sessionStorage.removeItem("socialLoginAuthData");
        }
    }
    getSocialLoginAuthData() {
        const socialLoginAuthDataString = sessionStorage.getItem("socialLoginAuthData");
        return socialLoginAuthDataString ? JSON.parse(socialLoginAuthDataString) : null;
    }
    setPatientConsentSession(session) {
        if (session) {
            sessionStorage.setItem("patientConsentSession", JSON.stringify(session));
        }
        else {
            sessionStorage.removeItem("patientConsentSession");
        }
    }
    getPatientConsentSession() {
        const sessionString = sessionStorage.getItem("patientConsentSession");
        return sessionString ? JSON.parse(sessionString) : null;
    }
    setInMemoryLanguage(language) {
        if (language) {
            sessionStorage.setItem("inMemoryLanguage", language);
        }
        else {
            sessionStorage.removeItem("inMemoryLanguage");
        }
    }
    getInMemoryLanguage() {
        const language = sessionStorage.getItem("inMemoryLanguage");
        if (!language)
            return null;
        return asEnum(StudyTranslationLanguage)(language);
    }
    getDocumentRetrievalUIState() {
        const documentRetrievalStateString = sessionStorage.getItem("documentRetrievalState");
        return documentRetrievalStateString ? JSON.parse(documentRetrievalStateString) : null;
    }
    setDocumentRetrievalUIState(state) {
        if (state) {
            sessionStorage.setItem("documentRetrievalState", JSON.stringify(state));
        }
        else {
            sessionStorage.removeItem("documentRetrievalState");
        }
    }
    setClearFallbackData(clearFallbackData) {
        if (clearFallbackData) {
            sessionStorage.setItem("clearFallbackData", JSON.stringify(instanceToPlain(clearFallbackData)));
        }
        else {
            sessionStorage.removeItem("clearFallbackData");
        }
    }
    getClearFallbackData() {
        const clearFallbackData = sessionStorage.getItem("clearFallbackData");
        return clearFallbackData ? plainToInstance(ClearFallbackData, JSON.parse(clearFallbackData)) : null;
    }
    setSandboxLogin(isSandboxLogin) {
        if (isSandboxLogin) {
            localStorage.setItem("sandboxLoginData", JSON.stringify({ isSandboxLogin }));
        }
        else {
            localStorage.removeItem("sandboxLoginData");
        }
    }
    getSandboxLogin() {
        const sandboxLoginData = localStorage.getItem("sandboxLoginData");
        return sandboxLoginData ? plainToInstance(SandboxLoginData, JSON.parse(sandboxLoginData)) : null;
    }
    isSandboxLogin() {
        var _a, _b;
        return (_b = (_a = this.getSandboxLogin()) === null || _a === void 0 ? void 0 : _a.isSandboxLogin) !== null && _b !== void 0 ? _b : false;
    }
    setIdpHintForAIFlow(idpHint) {
        if (idpHint) {
            sessionStorage.setItem("idpHintForAIFlow", idpHint);
        }
        else {
            sessionStorage.removeItem("idpHintForAIFlow");
        }
    }
    getIdpHintForAIFlow() {
        return sessionStorage.getItem("idpHintForAIFlow");
    }
    setSignedExternalId(externalId) {
        console.log("Setting signed external ID in sessionStorage:", externalId);
        if (externalId) {
            sessionStorage.setItem("signedExternalId", externalId);
        }
        else {
            sessionStorage.removeItem("signedExternalId");
        }
    }
    getSignedExternalId() {
        console.log("Getting signed external ID from sessionStorage:", sessionStorage.getItem("signedExternalId"));
        return sessionStorage.getItem("signedExternalId");
    }
    setIAL2Info(ial2Info) {
        if (ial2Info) {
            localStorage.setItem("ial2Info", JSON.stringify(ial2Info));
        }
        else {
            localStorage.removeItem("ial2Info");
        }
    }
    getIAL2Info() {
        const ial2InfoString = localStorage.getItem("ial2Info");
        return ial2InfoString ? JSON.parse(ial2InfoString) : null;
    }
    setAccountLinkingData(accountLinkingData) {
        if (accountLinkingData) {
            sessionStorage.setItem("accountLinkingData", JSON.stringify(accountLinkingData));
        }
        else {
            sessionStorage.removeItem("accountLinkingData");
        }
    }
    getAccountLinkingData() {
        const accountLinkingDataString = sessionStorage.getItem("accountLinkingData");
        return accountLinkingDataString ? JSON.parse(accountLinkingDataString) : null;
    }
    setConflictResolutionData(conflictResolutionData) {
        if (conflictResolutionData) {
            sessionStorage.setItem("conflictResolutionData", JSON.stringify(conflictResolutionData));
        }
        else {
            sessionStorage.removeItem("conflictResolutionData");
        }
    }
    getConflictResolutionData() {
        const conflictResolutionDataString = sessionStorage.getItem("conflictResolutionData");
        return conflictResolutionDataString ? JSON.parse(conflictResolutionDataString) : null;
    }
    setPendingSocialLoginData(data) {
        if (data) {
            sessionStorage.setItem("pendingSocialLoginData", JSON.stringify(data));
        }
        else {
            sessionStorage.removeItem("pendingSocialLoginData");
        }
    }
    getPendingSocialLoginData() {
        const dataString = sessionStorage.getItem("pendingSocialLoginData");
        return dataString ? JSON.parse(dataString) : null;
    }
    setRedirectUriForHostedFlow(redirectUri) {
        if (redirectUri) {
            sessionStorage.setItem("hostedFlowRedirectUri", redirectUri);
        }
        else {
            sessionStorage.removeItem("hostedFlowRedirectUri");
        }
    }
    getRedirectUriForHostedFlow() {
        return sessionStorage.getItem("hostedFlowRedirectUri");
    }
    setAddRecordLocationsDirectly(addRecordLocationsDirectly) {
        if (addRecordLocationsDirectly) {
            sessionStorage.setItem("addRecordLocationsDirectly", "true");
        }
        else {
            sessionStorage.removeItem("addRecordLocationsDirectly");
        }
    }
    getAddRecordLocationsDirectly() {
        return sessionStorage.getItem("addRecordLocationsDirectly") === "true";
    }
    setBrandId(brandId) {
        if (brandId) {
            sessionStorage.setItem("brandId", brandId);
        }
        else {
            sessionStorage.removeItem("brandId");
        }
    }
    getBrandId() {
        return sessionStorage.getItem("brandId");
    }
    setPortalId(portalId) {
        if (portalId) {
            sessionStorage.setItem("portalId", portalId);
        }
        else {
            sessionStorage.removeItem("portalId");
        }
    }
    getPortalId() {
        return sessionStorage.getItem("portalId");
    }
    setEndpointId(endpointId) {
        if (endpointId) {
            sessionStorage.setItem("endpointId", endpointId);
        }
        else {
            sessionStorage.removeItem("endpointId");
        }
    }
    getEndpointId() {
        return sessionStorage.getItem("endpointId");
    }
    setIss(iss) {
        if (iss) {
            sessionStorage.setItem("iss", iss);
        }
        else {
            sessionStorage.removeItem("iss");
        }
    }
    getIss() {
        return sessionStorage.getItem("iss");
    }
    setHostedSsoState(state) {
        if (state) {
            sessionStorage.setItem("hostedSsoState", state);
        }
        else {
            sessionStorage.removeItem("hostedSsoState");
        }
    }
    getHostedSsoState() {
        return sessionStorage.getItem("hostedSsoState");
    }
    setHostedSsoStudyId(studyId) {
        if (studyId) {
            sessionStorage.setItem("hostedSsoStudyId", studyId);
        }
        else {
            sessionStorage.removeItem("hostedSsoStudyId");
        }
    }
    getHostedSsoStudyId() {
        return sessionStorage.getItem("hostedSsoStudyId");
    }
    setHostedFlowStudyId(studyId) {
        if (studyId) {
            sessionStorage.setItem("hostedFlowStudyId", studyId);
        }
        else {
            sessionStorage.removeItem("hostedFlowStudyId");
        }
    }
    getHostedFlowStudyId() {
        return sessionStorage.getItem("hostedFlowStudyId");
    }
    clearHostedFlowSessionState() {
        sessionStorage.removeItem("hostedFlowStudyId");
        sessionStorage.removeItem("hostedFlowRedirectUri");
        sessionStorage.removeItem("addRecordLocationsDirectly");
        sessionStorage.removeItem("brandId");
        sessionStorage.removeItem("portalId");
        sessionStorage.removeItem("endpointId");
        sessionStorage.removeItem("signedExternalId");
        sessionStorage.removeItem("iss");
        sessionStorage.removeItem("hostedSsoState");
        sessionStorage.removeItem("hostedSsoStudyId");
    }
    setConsentDeferredVerificationStash(data) {
        if (data) {
            sessionStorage.setItem("consentDeferredVerificationStash", JSON.stringify(data));
        }
        else {
            sessionStorage.removeItem("consentDeferredVerificationStash");
        }
    }
    getConsentDeferredVerificationStash() {
        const raw = sessionStorage.getItem("consentDeferredVerificationStash");
        return raw ? JSON.parse(raw) : null;
    }
    setPendingSocialLoginSuccessMarker(marker) {
        if (marker) {
            sessionStorage.setItem("pendingSocialLoginSuccessMarker", JSON.stringify(marker));
        }
        else {
            sessionStorage.removeItem("pendingSocialLoginSuccessMarker");
        }
    }
    getPendingSocialLoginSuccessMarker() {
        const raw = sessionStorage.getItem("pendingSocialLoginSuccessMarker");
        return raw ? JSON.parse(raw) : null;
    }
    consentFlowStartedStorageKey(studyId, isDataUpdate) {
        return `consentFlowStarted:${studyId !== null && studyId !== void 0 ? studyId : "ias"}:${isDataUpdate}`;
    }
    hasConsentFlowStarted(studyId, isDataUpdate) {
        return sessionStorage.getItem(this.consentFlowStartedStorageKey(studyId, isDataUpdate)) === "true";
    }
    markConsentFlowStarted(studyId, isDataUpdate) {
        sessionStorage.setItem(this.consentFlowStartedStorageKey(studyId, isDataUpdate), "true");
    }
    clearConsentFlowStarted(studyId, isDataUpdate) {
        sessionStorage.removeItem(this.consentFlowStartedStorageKey(studyId, isDataUpdate));
    }
}
